import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';

/*
 * The cart's "Add 1 more item for 15% off" line (snippets/cart-summary.liquid) assumes the customer has no
 * code of their own. Liquid only sees a code once it applies, so a code that's waiting on a minimum
 * (e.g. a 20% sign-up code for 2+ items) is invisible to it. This reads the cart's codes from /cart.js and,
 * while one is waiting, swaps in a line naming that code.
 */

/* How long the line stays hidden waiting for /cart.js before falling back to the 15% offer */
const CHECK_TIMEOUT = 1500;

/* Sign-up forms attach their code a moment after the form closes */
const FORM_RECHECK_DELAYS = [1000, 3000];

/* How long to wait for a waiting code's value (e.g. "20% OFF") before showing the line without it */
const VALUE_LOOKUP_TIMEOUT = 1500;

const STOREFRONT_API_URL = '/api/2026-01/graphql.json';
const VALUE_STORAGE_PREFIX = 'veil:discount-value:';

/* Prices a throwaway cart with the code applied, to read what the code is worth */
const PRICE_CODE_MUTATION = `
  mutation PriceCode($lines: [CartLineInput!], $codes: [String!]) {
    cartCreate(input: { lines: $lines, discountCodes: $codes }) {
      cart {
        discountAllocations { ...CodeValue }
        lines(first: 50) { nodes { discountAllocations { ...CodeValue } } }
      }
    }
  }
  fragment CodeValue on CartDiscountAllocation {
    ... on CartCodeDiscountAllocation { code }
    discountApplication {
      value {
        ... on PricingPercentageValue { percentage }
        ... on MoneyV2 { amount currencyCode }
      }
    }
  }
`;

/**
 * The code on the cart as last read from /cart.js. Only kept for this page: a code remembered across
 * pages could be one that was already used at checkout.
 * @type {{ code: string, applicable: boolean } | null}
 */
let cartCode = null;

/* Codes the cart has already been re-rendered for, so a code Liquid never shows (e.g. free shipping) can't loop */
const rerenderedFor = new Set();

/** @type {Promise<void> | null} */
let activeRefresh = null;
/** @type {Promise<void> | null} */
let queuedRefresh = null;

/**
 * Re-reads the cart's codes. Calls made while a read is running share one follow-up read, so the
 * returned promise always settles after a read that started after the call.
 * @returns {Promise<void>}
 */
function refreshCartCode() {
  if (!activeRefresh) {
    activeRefresh = readCartCode().finally(() => {
      activeRefresh = null;
    });
    return activeRefresh;
  }

  queuedRefresh ??= activeRefresh.then(() => {
    queuedRefresh = null;
    return refreshCartCode();
  });
  return queuedRefresh;
}

async function readCartCode() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const cart = await response.json();
    const codes = (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).filter((discount) => discount?.code);
    const code = codes.find((discount) => discount.applicable === false) ?? codes[0];
    cartCode = code ? { code: String(code.code), applicable: code.applicable !== false } : null;

    /* Hold the update until the waiting code's value is known, so the line doesn't change twice */
    if (cartCode && !cartCode.applicable) {
      await Promise.race([
        lookUpCodeValue(cartCode.code, cart.items),
        new Promise((resolve) => setTimeout(resolve, VALUE_LOOKUP_TIMEOUT)),
      ]);
    }

    /* A code that applies while the "nothing applied" card is on the page means the card is out of date
       (e.g. a sign-up form added the code after the page loaded), so have the cart re-render from the server */
    if (cartCode?.applicable && !rerenderedFor.has(cartCode.code) && document.querySelector('cart-savings-message')) {
      rerenderedFor.add(cartCode.code);
      document.dispatchEvent(new DiscountUpdateEvent(cart, 'cart-savings-message'));
    }

    for (const message of document.querySelectorAll('cart-savings-message')) {
      if (message instanceof CartSavingsMessage) message.update();
    }
  } catch {}
}

function recheckIfShown() {
  if (document.querySelector('cart-savings-message')) refreshCartCode();
}

/**
 * What each waiting code is worth, e.g. "20% OFF", or '' when it couldn't be worked out.
 * A code's value never changes, so it's also kept for the rest of the visit.
 * @type {Map<string, string>}
 */
const codeValues = new Map();
/** @type {Map<string, Promise<void>>} */
const valueLookups = new Map();

/**
 * @param {string} code
 * @returns {string | undefined} undefined while not yet looked up
 */
function knownCodeValue(code) {
  if (!codeValues.has(code)) {
    try {
      const stored = sessionStorage.getItem(VALUE_STORAGE_PREFIX + code);
      if (stored) codeValues.set(code, stored);
    } catch {}
  }
  return codeValues.get(code);
}

/**
 * @param {string} code
 * @param {Array<{ variant_id: number, quantity: number }>} items - The cart's lines
 * @returns {Promise<void>}
 */
function lookUpCodeValue(code, items) {
  if (knownCodeValue(code) !== undefined) return Promise.resolve();

  let lookup = valueLookups.get(code);
  if (!lookup) {
    lookup = priceCode(code, items)
      .catch(() => '')
      .then((value) => {
        codeValues.set(code, value);
        if (!value) return;
        try {
          sessionStorage.setItem(VALUE_STORAGE_PREFIX + code, value);
        } catch {}
      });
    valueLookups.set(code, lookup);
  }
  return lookup;
}

/**
 * Asks Shopify what the code is worth by pricing a throwaway cart (never shown to the shopper) with the
 * cart's items plus one more, like the message suggests, then with a larger quantity in case the code needs more.
 * @param {string} code
 * @param {Array<{ variant_id: number, quantity: number }>} items
 * @returns {Promise<string>}
 */
async function priceCode(code, items) {
  const lines = (Array.isArray(items) ? items : [])
    .filter((item) => item?.variant_id && item.quantity > 0)
    .map((item) => ({ merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity }));
  const [first] = lines;
  if (!first) return '';

  const attempts = [
    [{ ...first, quantity: first.quantity + 1 }, ...lines.slice(1)],
    [{ ...first, quantity: Math.max(first.quantity + 1, 5) }],
  ];

  for (const attemptLines of attempts) {
    const value = await readCodeValue(code, attemptLines);
    if (value) return value;
  }
  return '';
}

/**
 * @param {string} code
 * @param {Array<{ merchandiseId: string, quantity: number }>} lines
 * @returns {Promise<string>}
 */
async function readCodeValue(code, lines) {
  const response = await isolatedFetch(new URL(STOREFRONT_API_URL, window.location.origin), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query: PRICE_CODE_MUTATION, variables: { lines, codes: [code] } }),
  });
  if (!response.ok) return '';

  const cart = (await response.json())?.data?.cartCreate?.cart;
  const allocations = [
    ...(cart?.discountAllocations ?? []),
    ...(cart?.lines?.nodes ?? []).flatMap((line) => line?.discountAllocations ?? []),
  ];
  const value = allocations.find((allocation) => allocation?.code?.toUpperCase() === code.toUpperCase())
    ?.discountApplication?.value;

  if (typeof value?.percentage === 'number') {
    return `${Math.round(value.percentage * 100) / 100}% OFF`;
  }
  if (value?.amount && value.currencyCode) {
    const amount = Number(value.amount);
    const money = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: value.currencyCode,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
    return `${money} OFF`;
  }
  return '';
}

/** @type {typeof fetch | null} */
let isolatedFetchFn = null;

/**
 * Runs the pricing request from a hidden blank frame. Shopify's tracking watches this page's requests and would
 * report the throwaway cart to analytics and ad pixels as a real add to cart.
 * @param {RequestInfo | URL} input
 * @param {RequestInit} init
 * @returns {Promise<Response>}
 */
function isolatedFetch(input, init) {
  if (!isolatedFetchFn) {
    const frame = document.createElement('iframe');
    frame.hidden = true;
    frame.tabIndex = -1;
    frame.setAttribute('aria-hidden', 'true');
    frame.title = 'Discount pricing';
    document.body.append(frame);
    const frameWindow = frame.contentWindow;
    if (!frameWindow) return Promise.reject(new Error('No frame window'));
    isolatedFetchFn = frameWindow.fetch.bind(frameWindow);
  }
  return isolatedFetchFn(input, init);
}

class CartSavingsMessage extends HTMLElement {
  /* The cart is re-rendered and morphed on every change, which resets this element's attributes and text */
  #mutationObserver = new MutationObserver(() => this.update());

  /* Re-check when the message comes into view, e.g. the drawer opens after a sign-up form added a code */
  #intersectionObserver = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) refreshCartCode();
  });

  /* Line held back until the cart has been read */
  #checking = false;

  /* The cart has been read since this card was rendered */
  #confirmed = false;

  connectedCallback() {
    /* With no code known, a sign-up form may have just added one, so hold the line back rather than
       flashing the 15% offer. A known code (e.g. the cart just went from 2 items to 1) is shown straight away. */
    this.#checking = !cartCode;
    this.#confirmed = false;
    this.update();
    this.#mutationObserver.observe(this, { attributes: true, childList: true, subtree: true, characterData: true });
    this.#intersectionObserver.observe(this);

    refreshCartCode().then(() => {
      this.#confirmed = true;
      this.#checking = false;
      this.update();
    });
    setTimeout(() => {
      if (!this.#checking) return;
      this.#checking = false;
      this.update();
    }, CHECK_TIMEOUT);
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  update() {
    this.toggleAttribute('data-checking', this.#checking);

    /* This card only renders when nothing applies, so until the cart is read a known code is assumed to be
       waiting; after that, only a code the cart says doesn't apply yet is shown */
    const code = cartCode && (!this.#confirmed || !cartCode.applicable) ? cartCode.code : '';

    if ((this.getAttribute('data-pending-code') ?? '') !== code) {
      if (code) this.setAttribute('data-pending-code', code);
      else this.removeAttribute('data-pending-code');
    }

    const slot = this.querySelector('[data-savings-code]');
    if (slot && slot.textContent !== code) slot.textContent = code;

    /* e.g. "20% OFF " so the line reads "use your 20% OFF code BN-38B7977A" */
    const codeValue = code ? knownCodeValue(code) : '';
    const valueText = codeValue ? `${codeValue} ` : '';
    const valueSlot = this.querySelector('[data-savings-value]');
    if (valueSlot && valueSlot.textContent !== valueText) valueSlot.textContent = valueText;
  }
}

if (!customElements.get('cart-savings-message')) {
  customElements.define('cart-savings-message', CartSavingsMessage);
}

/* A code can start or stop applying whenever the cart changes */
for (const eventName of [ThemeEvents.cartUpdate, ThemeEvents.discountUpdate]) {
  document.addEventListener(eventName, recheckIfShown);
}

/*
 * Sign-up pop-ups (Privy, Klaviyo) attach their code by requesting /discount/<code> in the background, often
 * while the cart is already on screen, and the theme gets no cart event for it. Watch for those requests and
 * have the cart re-render, so the totals and this message pick the code up.
 */

/**
 * @param {unknown} target - A fetch input or XHR URL
 */
function isDiscountLink(target) {
  try {
    const href = target instanceof Request ? target.url : String(target);
    const url = new URL(href, window.location.href);
    return url.origin === window.location.origin && /(^|\/)discount\/[^/]+/.test(url.pathname);
  } catch {
    return false;
  }
}

function onDiscountLinkLoaded() {
  document.dispatchEvent(new DiscountUpdateEvent({}, 'cart-savings-message'));
}

const nativeFetch = window.fetch;
window.fetch = function (input, init) {
  const request = nativeFetch.call(window, input, init);
  if (isDiscountLink(input)) request.then(onDiscountLinkLoaded, () => {});
  return request;
};

const nativeXhrOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (/** @type {string} */ method, /** @type {string | URL} */ url, ...rest) {
  if (isDiscountLink(url)) this.addEventListener('loadend', onDiscountLinkLoaded, { once: true });
  // @ts-ignore - forwards the optional async/user/password arguments unchanged
  return nativeXhrOpen.call(this, method, url, ...rest);
};

/* A sign-up form can add a code while the card is already on screen (cart page, or an open drawer) */
window.addEventListener('klaviyoForms', (event) => {
  const type = /** @type {CustomEvent} */ (event).detail?.type;
  if (type !== 'submit' && type !== 'stepSubmit' && type !== 'close') return;
  for (const delay of FORM_RECHECK_DELAYS) setTimeout(recheckIfShown, delay);
});
window.addEventListener('focus', recheckIfShown);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') recheckIfShown();
});
